import {
  DataTable,
  RadioButton,
  DataTableCustomRenderProps,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Checkbox,
} from 'carbon-components-react';
import React, { useEffect, useState } from 'react';
import { que2bValueProps } from '../../SPSSPurchaseAdvisorSelfAssessment';
import { segmentsTracking } from 'src/common/lib/tracker';
import { CheckmarkFilled20 } from '@carbon/icons-react';
import {
  convertToString,
  TranslationFunction,
} from 'src/common/hooks/widgets/useTranslation';

type Step2Props = {
  pathSelected: string;
  que2AValue: string;
  setQue2AValue: React.Dispatch<React.SetStateAction<string>>;
  checkbox: boolean;
  que2BValue: que2bValueProps;
  setQue2BValue: React.Dispatch<React.SetStateAction<que2bValueProps>>;
  setIsRequired: React.Dispatch<React.SetStateAction<boolean>>;
  isRequired: boolean;
  que1Value: string;
  useTranslation: TranslationFunction;
};

const Step2 = ({
  pathSelected,
  que2AValue,
  setQue2AValue,
  checkbox,
  que2BValue,
  setQue2BValue,
  setIsRequired,
  que1Value,
  useTranslation,
}: Step2Props) => {
  const [optionA, setOptionA] = useState(false);
  const [optionB, setOptionB] = useState(false);
  const [optionC, setOptionC] = useState(false);
  const [optionD, setOptionD] = useState(false);
  const handleClick = (value: string) => {
    setIsRequired(false);
    if (value === 'A') {
      setOptionA(true);
      setOptionB(false);
      setOptionC(false);
      setOptionD(false);
    } else if (value === 'B') {
      setOptionA(false);
      setOptionB(true);
      setOptionC(false);
      setOptionD(false);
    } else if (value === 'C') {
      setOptionA(false);
      setOptionB(false);
      setOptionC(true);
      setOptionD(false);
    } else if (value === 'D') {
      setOptionA(false);
      setOptionB(false);
      setOptionC(false);
      setOptionD(true);
    }
    setQue2AValue(value);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'SPSS Purchasing Advisor Self Assessment',
      object: 'SPSS Purchasing Advisor Self Assessment page',
      resultValue: 'CTA clicked',
      CTA: 'Q2 radio selection',
      location: `${pathSelected} - ${que1Value} - Feature selected`,
      action: value,
      field: '',
    });
  };

  const headers1 = [
    {
      key: 'option',
      header: '',
    },
    {
      key: 'optionA',
      header: '',
    },
    {
      key: 'optionB',
      header: '',
    },
    {
      key: 'optionC',
      header: '',
    },
    {
      key: 'optionD',
      header: '',
    },
  ];

  const radioOption = <CheckmarkFilled20 fill="#24A148" />;
  const checkedOption = !checkbox && <CheckmarkFilled20 fill="#24A148" />;

  useEffect(() => {
    if (que2AValue === 'A') {
      setOptionA(true);
      setOptionB(false);
      setOptionC(false);
      setOptionD(false);
    } else if (que2AValue === 'B') {
      setOptionA(false);
      setOptionB(true);
      setOptionC(false);
      setOptionD(false);
    } else if (que2AValue === 'C') {
      setOptionA(false);
      setOptionB(false);
      setOptionC(true);
      setOptionD(false);
    } else if (que2AValue === 'D') {
      setOptionA(false);
      setOptionB(false);
      setOptionC(false);
      setOptionD(true);
    }
  }, [que2AValue]);

  useEffect(() => {
    if (!que2AValue && !checkbox) {
      setIsRequired(true);
    } else if (
      !(
        que2BValue.options.Q3BA1.value ||
        que2BValue.options.Q3BA2.value ||
        que2BValue.options.Q3BA3.value ||
        que2BValue.options.Q3BA4.value
      ) &&
      checkbox
    ) {
      setIsRequired(true);
    } else {
      setIsRequired(false);
    }
  }, [que2AValue, que2BValue]);

  const rows1 = [
    {
      id: 'rows1',
      option: convertToString(useTranslation('ques2_stats_base_text', true)),
      optionA: radioOption,
      optionB: checkedOption,
      optionC: checkedOption,
      optionD: checkedOption,
    },
    {
      id: 'rows2',
      option: convertToString(useTranslation('ques2_data_prep_text', true)),
      optionA: radioOption,
      optionB: checkedOption,
      optionC: checkedOption,
      optionD: checkedOption,
    },
    {
      id: 'rows3',
      option: convertToString(useTranslation('ques2_boot_strap_text', true)),
      optionA: radioOption,
      optionB: checkedOption,
      optionC: checkedOption,
      optionD: checkedOption,
    },
    {
      id: 'rows4',
      option: convertToString(useTranslation('ques2_adv_stats_text', true)),
      optionB: radioOption,
      optionC: checkedOption,
      optionD: checkedOption,
    },
    {
      id: 'rows5',
      option: convertToString(useTranslation('ques2_regression_text', true)),
      optionB: radioOption,
      optionC: checkedOption,
      optionD: checkedOption,
    },
    {
      id: 'rows6',
      option: convertToString(useTranslation('ques2_custom_table_text', true)),
      optionB: radioOption,
      optionC: checkedOption,
      optionD: checkedOption,
    },
    {
      id: 'rows7',
      option: convertToString(useTranslation('ques2_miss_val_text', true)),
      optionC: radioOption,
      optionD: checkedOption,
    },
    {
      id: 'rows8',
      option: convertToString(useTranslation('ques2_catg_text', true)),
      optionC: radioOption,
      optionD: checkedOption,
    },
    {
      id: 'rows9',
      option: !checkbox
        ? convertToString(useTranslation('ques2_forecasting_text', true))
        : convertToString(useTranslation('ques2_comp_samp_text', true)),
      optionC: radioOption,
      optionD: checkedOption,
    },
    {
      id: 'rows10',
      option: !checkbox
        ? convertToString(useTranslation('ques2_decs_tree_text', true))
        : convertToString(useTranslation('ques2_conjoint_text', true)),
      optionC: radioOption,
      optionD: checkedOption,
    },
    {
      id: 'rows11',
      option: !checkbox
        ? convertToString(useTranslation('ques2_neural_network_text', true))
        : convertToString(useTranslation('ques2_exact_test_text', true)),
      optionC: checkbox && radioOption,
      optionD: checkedOption,
    },
    {
      id: 'rows12',
      option: !checkbox
        ? convertToString(useTranslation('ques2_direct_markt_text', true))
        : convertToString(useTranslation('ques2_forecasting_text', true)),
      optionD: radioOption,
    },
    {
      id: 'rows13',
      option: !checkbox
        ? convertToString(useTranslation('ques2_comp_samp_text', true))
        : convertToString(useTranslation('ques2_decs_tree_text', true)),
      optionD: radioOption,
    },
    {
      id: 'rows14',
      option: !checkbox
        ? convertToString(useTranslation('ques2_conjoint_text', true))
        : convertToString(useTranslation('ques2_neural_network_text', true)),
      optionD: radioOption,
    },
    {
      id: 'rows15',
      option: !checkbox
        ? convertToString(useTranslation('ques2_exact_test_text', true))
        : convertToString(useTranslation('ques2_direct_markt_text', true)),
      optionD: radioOption,
    },
  ];

  const handleCheck = (checked: boolean, value: string, id: string) => {
    segmentsTracking('CTA Clicked', {
      pageTitle: 'SPSS Purchasing Advisor Self Assessment',
      object: 'SPSS Purchasing Advisor Self Assessment page',
      resultValue: 'CTA clicked',
      CTA: 'Q2 checkbox selection',
      location: `${pathSelected} - ${que1Value} - Feature selected`,
      action: value,
      field: '',
    });
    setIsRequired(false);
    if (checked) {
      setQue2BValue({
        ...que2BValue,
        options: {
          ...que2BValue.options,
          [id]: {
            value: value,
            checked: true,
          },
        },
      });
    } else {
      setQue2BValue({
        ...que2BValue,
        options: {
          ...que2BValue.options,
          [id]: {
            value: '',
            checked: false,
          },
        },
      });
    }
  };

  return (
    <div>
      {!checkbox ? (
        <div>
          <div className="page_heading_3">{useTranslation('ques2_text')}</div>
          <DataTable
            size="sm"
            rows={rows1}
            headers={headers1}
            stickyHeader={false}
          >
            {({
              rows,
              getTableProps,
              getRowProps,
              getTableContainerProps,
            }: DataTableCustomRenderProps) => (
              <TableContainer
                className="spss-mar2"
                {...getTableContainerProps()}
              >
                <Table style={{ position: 'relative' }} {...getTableProps()}>
                  <TableHead
                    style={{ position: 'sticky', top: 0 }}
                    className="header-bakground"
                  >
                    <TableRow>
                      <TableHeader></TableHeader>
                      <TableHeader>
                        <RadioButton
                          id={'a'}
                          value={que2AValue}
                          checked={optionA}
                          onClick={() => {
                            handleClick('A');
                          }}
                          className="radio-check-mar"
                        />{' '}
                        <span className="label-mar">A</span>
                      </TableHeader>
                      <TableHeader>
                        <RadioButton
                          id={'b'}
                          value={que2AValue}
                          checked={optionB}
                          onClick={() => {
                            handleClick('B');
                          }}
                          className="radio-check-mar"
                        />
                        <span className="label-mar">B</span>
                      </TableHeader>
                      <TableHeader>
                        <RadioButton
                          id={'c'}
                          value={que2AValue}
                          checked={optionC}
                          onClick={() => {
                            handleClick('C');
                          }}
                          className="radio-check-mar"
                        />
                        <span className="label-mar">C</span>
                      </TableHeader>
                      <TableHeader>
                        <RadioButton
                          id={'d'}
                          value={que2AValue}
                          checked={optionD}
                          onClick={() => {
                            handleClick('D');
                          }}
                          className="radio-check-mar"
                        />
                        <span className="label-mar">D</span>
                      </TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow {...getRowProps({ row })}>
                        {row.cells.map((cell) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        </div>
      ) : (
        <div>
          <div className="page_heading_3">{useTranslation('ques2_text')}</div>
          <div className="multiOption-text">
            {useTranslation('ques2_italic_text')}
          </div>
          <DataTable
            size="sm"
            rows={rows1}
            headers={headers1}
            stickyHeader={false}
          >
            {({
              rows,
              getTableProps,
              getRowProps,
              getTableContainerProps,
            }: DataTableCustomRenderProps) => (
              <TableContainer {...getTableContainerProps()}>
                <Table style={{ position: 'relative' }} {...getTableProps()}>
                  <TableHead
                    style={{ position: 'sticky', top: 0 }}
                    className="header-bakground"
                  >
                    <TableRow>
                      <TableHeader></TableHeader>
                      <TableHeader>
                        <Checkbox
                          labelText={''}
                          id="Q3BA1"
                          checked={que2BValue.options.Q3BA1.checked}
                          onChange={(checked: boolean, id: string) =>
                            handleCheck(checked, 'A', id)
                          }
                        />
                        <span className="label-mar">A</span>
                      </TableHeader>
                      <TableHeader className="checkbox-div-mar">
                        <Checkbox
                          labelText={''}
                          id="Q3BA2"
                          checked={que2BValue.options.Q3BA2.checked}
                          onChange={(checked: boolean, id: string) =>
                            handleCheck(checked, 'B', id)
                          }
                        />
                        <span className="label-mar">B</span>
                      </TableHeader>
                      <TableHeader className="checkbox-div-mar">
                        <Checkbox
                          labelText={''}
                          id="Q3BA3"
                          checked={que2BValue.options.Q3BA3.checked}
                          onChange={(checked: boolean, id: string) =>
                            handleCheck(checked, 'C', id)
                          }
                        />
                        <span className="label-mar">C</span>
                      </TableHeader>
                      <TableHeader className="checkbox-div-mar">
                        <Checkbox
                          labelText={''}
                          id="Q3BA4"
                          checked={que2BValue.options.Q3BA4.checked}
                          onChange={(checked: boolean, id: string) =>
                            handleCheck(checked, 'D', id)
                          }
                        />
                        <span className="label-mar">D</span>
                      </TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow {...getRowProps({ row })}>
                        {row.cells.map((cell) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        </div>
      )}
    </div>
  );
};

export default Step2;
